import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { getUrlDomain, normalizeExternalUrl } from "../utility/urlUtils";

const PRIMARY = "#046287";

/**
 * "You're leaving Ngwaahia" confirmation for a seller-supplied link. Shows the
 * real domain in bold so a link that reads like one site but goes to another
 * is easy to spot.
 */
export const ExternalLinkDialog = ({ url, warnUnrecognized, onClose }) => {
  const domain = getUrlDomain(url);
  const handleOpen = () => {
    window.open(url, "_blank", "noopener,noreferrer");
    onClose();
  };

  return (
    <Dialog
      open={Boolean(url && domain)}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ pr: 6, fontWeight: 700 }}>
        You're leaving Ngwaahia
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          This link opens in a new tab:
        </Typography>
        <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
          {domain}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", wordBreak: "break-all" }}
        >
          {url}
        </Typography>
        {warnUnrecognized && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mt: 2,
              p: 1.25,
              bgcolor: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: 2,
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 18, color: "#b45309" }} />
            <Typography variant="caption" sx={{ color: "#92400e" }}>
              This isn't a courier site we recognize. Check the address before
              you continue.
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            mt: 2,
            p: 1.25,
            bgcolor: "#f4f4f4",
            borderRadius: 2,
          }}
        >
          <ShieldOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <Typography variant="caption" color="text.secondary">
            Ngwaahia doesn't control this site. Never pay or enter your password
            through a link from a seller.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, mt: 2.5 }}>
          <Button
            variant="text"
            onClick={onClose}
            sx={{ flex: 1, textTransform: "none", color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            endIcon={<OpenInNewIcon />}
            onClick={handleOpen}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: PRIMARY,
              "&:hover": { bgcolor: "#035070" },
            }}
          >
            Open link
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

/**
 * A seller-supplied website or social value. Rendered as a link (through the
 * leaving-Ngwaahia dialog) only when it's an https link; anything else, like
 * an @handle or an http:// site, stays plain text.
 */
const ExternalLink = ({ value, warnUnrecognized = false }) => {
  const [pendingUrl, setPendingUrl] = useState(null);
  const url = normalizeExternalUrl(value);
  if (!url) return value;

  return (
    <>
      <Link
        component="button"
        type="button"
        onClick={() => setPendingUrl(url)}
        sx={{
          color: PRIMARY,
          textAlign: "left",
          verticalAlign: "baseline",
          wordBreak: "break-all",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {value}
        <OpenInNewIcon sx={{ fontSize: 13 }} />
      </Link>
      <ExternalLinkDialog
        url={pendingUrl}
        warnUnrecognized={warnUnrecognized}
        onClose={() => setPendingUrl(null)}
      />
    </>
  );
};

export default ExternalLink;
